/**
 * Trade Logs Utility Functions
 * Correlation and formatting helpers for enhanced Trade Logs View #2
 */

/**
 * Extract ClOrdID from trade log message
 * @param {Object} tradeLog - Trade log object
 * @returns {string|null} - ClOrdID or null if not found
 */
export const extractClOrdID = (tradeLog) => {
    if (!tradeLog) {
        console.log('⚠️ extractClOrdID: tradeLog is null/undefined');
        return null;
    }

    // DEBUG: Log the entire tradeLog object structure (only first time)
    if (!window._tradeLogStructureLogged) {
        console.log('🔍 DEBUG: Trade Log Object Structure:', JSON.stringify(tradeLog, null, 2));
        window._tradeLogStructureLogged = true;
    }

    const message = tradeLog.message;

    // Pattern 1: "#211254731" format (order ID with hash)
    if (message) {
        const hashPattern = /#(\d+)/;
        const hashMatch = message.match(hashPattern);
        if (hashMatch) {
            console.log('✅ Extracted ClOrdID from # pattern:', hashMatch[1]);
            return hashMatch[1];
        }

        // Pattern 2: "ClOrdID=XXX" or "ClOrdID: XXX"
        const clOrdIDPattern = /ClOrdID[=:]\s*([^\s,;]+)/i;
        const clOrdIDMatch = message.match(clOrdIDPattern);
        if (clOrdIDMatch) {
            console.log('✅ Extracted ClOrdID from ClOrdID pattern:', clOrdIDMatch[1]);
            return clOrdIDMatch[1];
        }
    }

    // Pattern 3: Direct ClOrdID field on object
    const directClOrdID = tradeLog.clOrdId || tradeLog.clOrdID || tradeLog.ClOrdID || tradeLog.clOrderId;
    if (directClOrdID) {
        console.log('✅ Extracted ClOrdID from direct property:', directClOrdID);
        return String(directClOrdID);
    }

    console.log('❌ No ClOrdID found in trade log');
    return null;
};

/**
 * Correlate trade log with execution metrics by ClOrdID
 * @param {Object} tradeLog - Trade log object
 * @param {Array} executionMetricsList - Array of execution metrics
 * @returns {Object|null} - Matching execution metrics or null
 */
export const correlateExecutionMetrics = (tradeLog, executionMetricsList) => {
    if (!tradeLog || !executionMetricsList || executionMetricsList.length === 0) {
        console.log('⚠️ correlateExecutionMetrics: Missing data -', {
            hasTradeLog: !!tradeLog,
            metricsCount: executionMetricsList?.length || 0
        });
        return null;
    }

    // DEBUG: Log executionMetrics structure (only first time)
    if (!window._executionMetricsStructureLogged && executionMetricsList.length > 0) {
        console.log('🔍 DEBUG: ExecutionMetrics Object Structure:', JSON.stringify(executionMetricsList[0], null, 2));
        console.log('🔍 DEBUG: ExecutionMetrics available properties:', Object.keys(executionMetricsList[0]));

        // Log ALL ClOrdIDs in executionMetrics
        console.log('🔍 DEBUG: All ClOrdIDs in executionMetrics:');
        executionMetricsList.forEach((metric, index) => {
            const allPossibleIDs = {
                clOrdId: metric.clOrdId,
                clOrdID: metric.clOrdID,
                ClOrdID: metric.ClOrdID,
                clOrderId: metric.clOrderId,
                orderId: metric.orderId,
                orderID: metric.orderID,
                OrderId: metric.OrderId,
                id: metric.id
            };
            console.log(`  [${index}]:`, allPossibleIDs);
        });

        window._executionMetricsStructureLogged = true;
    }

    const clOrdID = extractClOrdID(tradeLog);
    if (!clOrdID) {
        console.log('❌ No ClOrdID extracted from trade log');
        return null;
    }

    // CRITICAL FIX: Convert to string and check clOrdId (lowercase 'd') first
    // Based on ExecutionMetrics component which successfully uses ex.clOrdId
    const match = executionMetricsList.find(metric => {
        // Convert all possible ID fields to string for comparison
        const metricClOrdID = String(
            metric.clOrdId ||      // PRIMARY: Used by ExecutionMetrics component
            metric.orderId ||      // SECONDARY: Also used by ExecutionMetrics component
            metric.clOrdID ||      // Fallback: uppercase D
            metric.ClOrdID ||      // Fallback: Pascal case
            metric.clOrderId ||    // Fallback: alternative naming
            ''
        );

        const extractedClOrdID = String(clOrdID);
        const isMatch = metricClOrdID === extractedClOrdID;

        if (isMatch) {
            console.log('🎯 MATCH FOUND! ClOrdID:', extractedClOrdID, '=', metricClOrdID);
        }

        return isMatch;
    });

    // DEBUG: Log correlation results
    if (match) {
        console.log('✅ Correlation SUCCESS - ClOrdID:', clOrdID, {
            latency: match.submissionToFillLatencyMs,
            slippage: match.slippagePips,
            spread: match.spreadAtSubmit
        });
    } else {
        console.log('❌ Correlation FAILED - No match for ClOrdID:', clOrdID, 'in', executionMetricsList.length, 'metrics');
    }

    return match || null;
};

/**
 * Get latency color based on milliseconds
 * @param {number} latencyMs - Latency in milliseconds
 * @returns {string} - Color hex code
 */
export const getLatencyColor = (latencyMs) => {
    if (latencyMs === null || latencyMs === undefined || isNaN(latencyMs)) {
        return '#6b7280'; // Gray for N/A
    }

    if (latencyMs < 200) return '#10b981'; // Green
    if (latencyMs < 500) return '#f59e0b'; // Yellow
    if (latencyMs < 1000) return '#f97316'; // Orange
    return '#ef4444'; // Red
};

/**
 * Get latency emoji indicator
 * @param {number} latencyMs - Latency in milliseconds
 * @returns {string} - Emoji indicator
 */
export const getLatencyEmoji = (latencyMs) => {
    if (latencyMs === null || latencyMs === undefined || isNaN(latencyMs)) {
        return '';
    }

    if (latencyMs < 200) return '⚡'; // Excellent
    if (latencyMs < 500) return '⏱️'; // Acceptable
    return '⚠️'; // Slow
};

/**
 * Get slippage color based on pips
 * @param {number} slippagePips - Slippage in pips
 * @returns {string} - Color hex code
 */
export const getSlippageColor = (slippagePips) => {
    if (slippagePips === null || slippagePips === undefined || isNaN(slippagePips)) {
        return '#6b7280'; // Gray for N/A
    }

    const absSlippage = Math.abs(slippagePips);

    if (absSlippage < 0.5) return '#10b981'; // Green
    if (absSlippage < 1.0) return '#f59e0b'; // Yellow
    if (absSlippage < 2.0) return '#f97316'; // Orange
    return '#ef4444'; // Red
};

/**
 * Get slippage emoji indicator
 * @param {number} slippagePips - Slippage in pips
 * @returns {string} - Emoji indicator
 */
export const getSlippageEmoji = (slippagePips) => {
    if (slippagePips === null || slippagePips === undefined || isNaN(slippagePips)) {
        return '';
    }

    const absSlippage = Math.abs(slippagePips);

    if (absSlippage < 0.5) return '💰'; // Low slippage
    if (absSlippage < 1.5) return '📊'; // Moderate slippage
    return '📉'; // High slippage
};

/**
 * Calculate quality score based on latency and slippage
 * (Reusing logic from ExecutionMetrics utils.js)
 * @param {number} latencyMs - Latency in milliseconds
 * @param {number} slippagePips - Slippage in pips
 * @returns {Object} - { score: number, grade: string, color: string, emoji: string }
 */
export const calculateQualityScore = (latencyMs, slippagePips) => {
    if (latencyMs === null || latencyMs === undefined || isNaN(latencyMs)) {
        return { score: 0, grade: 'N/A', color: '#6b7280', emoji: '' };
    }

    // Latency scoring (0-50 points)
    let latencyScore = 0;
    if (latencyMs < 100) latencyScore = 50;
    else if (latencyMs < 200) latencyScore = 45;
    else if (latencyMs < 500) latencyScore = 35;
    else if (latencyMs < 1000) latencyScore = 20;
    else latencyScore = 5;

    // Slippage scoring (0-50 points)
    const absSlippage = Math.abs(slippagePips || 0);
    let slippageScore = 0;
    if (absSlippage < 0.3) slippageScore = 50;
    else if (absSlippage < 0.5) slippageScore = 45;
    else if (absSlippage < 1.0) slippageScore = 35;
    else if (absSlippage < 2.0) slippageScore = 20;
    else slippageScore = 5;

    const totalScore = latencyScore + slippageScore;

    // Grade, color, and emoji
    let grade, color, emoji;
    if (totalScore >= 90) {
        grade = 'Excellent';
        color = '#10b981'; // Green
        emoji = '✅';
    } else if (totalScore >= 70) {
        grade = 'Good';
        color = '#f59e0b'; // Yellow
        emoji = '⚡';
    } else if (totalScore >= 50) {
        grade = 'Acceptable';
        color = '#f97316'; // Orange
        emoji = '⚠️';
    } else {
        grade = 'Poor';
        color = '#ef4444'; // Red
        emoji = '❌';
    }

    return { score: totalScore, grade, color, emoji };
};

/**
 * Format latency with milliseconds unit
 * @param {number} latencyMs - Latency in milliseconds
 * @returns {string} - Formatted latency
 */
export const formatLatency = (latencyMs) => {
    if (latencyMs === null || latencyMs === undefined || isNaN(latencyMs)) {
        return '--';
    }
    return `${Math.round(latencyMs)}ms`;
};

/**
 * Format slippage with sign and pips unit
 * @param {number} slippagePips - Slippage in pips
 * @returns {string} - Formatted slippage
 */
export const formatSlippage = (slippagePips) => {
    if (slippagePips === null || slippagePips === undefined || isNaN(slippagePips)) {
        return '--';
    }
    const sign = slippagePips >= 0 ? '+' : '';
    return `${sign}${slippagePips.toFixed(2)}p`;
};

/**
 * Format spread in pips
 * @param {number} spreadPips - Spread in pips
 * @returns {string} - Formatted spread
 */
export const formatSpread = (spreadPips) => {
    if (spreadPips === null || spreadPips === undefined || isNaN(spreadPips)) {
        return '--';
    }
    return `${spreadPips.toFixed(2)}p`;
};
